import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../components/LanguageContext";
import { useState, useEffect, useRef } from "react";

// Brand Carousel Component
function BrandCarousel() {
  const { t } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);
  const animationRef = useRef<number>();

  // Client brand logos - Updated to 17 brands
  const brands = [
    { id: 1, name: "Brand 1", logo: "/images/home/client_brands/brand_1.jpg" },
    { id: 2, name: "Brand 2", logo: "/images/home/client_brands/brand_2.jpg" },
    { id: 3, name: "Brand 3", logo: "/images/home/client_brands/brand_3.png" },
    { id: 4, name: "Brand 4", logo: "/images/home/client_brands/brand_4.png" },
    { id: 5, name: "Brand 5", logo: "/images/home/client_brands/brand_5.jpg" },
    { id: 6, name: "Brand 6", logo: "/images/home/client_brands/brand_6.png" },
    { id: 7, name: "Brand 7", logo: "/images/home/client_brands/brand_7.png" },
    { id: 8, name: "Brand 8", logo: "/images/home/client_brands/brand_8.png" },
    { id: 9, name: "Brand 9", logo: "/images/home/client_brands/brand_9.jpg" },
    { id: 10, name: "Brand 10", logo: "/images/home/client_brands/brand_10.png" },
    { id: 11, name: "Brand 11", logo: "/images/home/client_brands/brand_11.png" },
    { id: 12, name: "Brand 12", logo: "/images/home/client_brands/brand_12.png" },
    { id: 13, name: "Brand 13", logo: "/images/home/client_brands/brand_13.png" },
    { id: 14, name: "Brand 14", logo: "/images/home/client_brands/brand_14.png" },
    { id: 15, name: "Brand 15", logo: "/images/home/client_brands/brand_15.png" },
    { id: 16, name: "Brand 16", logo: "/images/home/client_brands/brand_16.png" },
    { id: 17, name: "Brand 17", logo: "/images/home/client_brands/brand_17.png" },
  ];

  // Duplicate brands for infinite scroll effect
  const duplicatedBrands = [...brands, ...brands, ...brands];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollPosition = 0;
    const scrollSpeed = 1.2; // Pixels per frame - increased by 1.2x for faster sliding
    const resetPosition = brands.length * 180; // Width of one set of brands

    const animate = () => {
      if (!isPaused && scrollContainer) {
        scrollPosition += scrollSpeed;
        
        // Reset position for infinite loop
        if (scrollPosition >= resetPosition) {
          scrollPosition = 0;
        }
        
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, brands.length]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      viewport={{ once: true }}
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Gradient Overlays */}
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
      
      {/* Scrolling Container */}
      <div 
        ref={scrollRef}
        className="flex gap-8 overflow-x-hidden scroll-smooth"
        style={{ scrollBehavior: 'auto' }}
      >
        {duplicatedBrands.map((brand, index) => (
          <motion.div
            key={`${brand.id}-${index}`}
            className="flex-shrink-0 w-40 h-24 bg-white border-2 border-slate-200 rounded-lg flex items-center justify-center hover:border-cyan-400/50 transition-all duration-300 hover:shadow-lg group"
            whileHover={{ y: -5 }}
          >
            {brand.logo ? (
              <img 
                src={brand.logo} 
                alt={brand.name}
                className="max-w-[120px] max-h-16 object-contain px-4 group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="text-center">
                <div className="text-xs text-slate-400 mb-1">{t('accessibility.logoText')}</div>
                <div className="text-sm text-slate-600 font-medium">{brand.name}</div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Pause Indicator */}
      <motion.div
        className={`absolute top-2 right-2 text-xs text-slate-400 transition-opacity duration-300 ${isPaused ? 'opacity-100' : 'opacity-0'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: isPaused ? 1 : 0 }}
      >
        Paused
      </motion.div>
    </motion.div>
  );
}

// Animated Counter Component
function AnimatedCounter({ end, suffix = "", prefix = "", duration = 0.8, format = false, inView = false }: { end: number; suffix?: string; prefix?: string; duration?: number; format?: boolean; inView?: boolean }) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (inView && !hasAnimated) {
      const startValue = 0;
      const startTime = Date.now();
      
      const updateCount = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / (duration * 1000), 1);
        
        // Linear progression for even speed
        const currentCount = Math.floor(startValue + (end - startValue) * progress);
        
        setCount(currentCount);
        
        if (progress < 1) {
          requestAnimationFrame(updateCount);
        } else {
          setCount(end);
        }
      };
      
      updateCount();
      setHasAnimated(true);
    }
  }, [end, duration, hasAnimated, inView]);

  // Format number with comma separator if needed
  const displayCount = format ? count.toLocaleString() : count;

  return <>{prefix}{displayCount}{suffix}</>;
}

export default function HomePage() {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [metricsInView, setMetricsInView] = useState(false);

  // Hero logo row. Vietnamese visitors get it without the Dianping logo
  // (hero-logos-vn): chosen here when the site is in Vietnamese; for Vietnamese
  // IPs / browser language the CF Worker swaps the file instead
  // (cloudflare-worker/src/index.js). "?v=2" makes browsers that cached the
  // file before that swap existed fetch it again.
  const heroLogos =
    currentLanguage === 'ja' ? '/images/home/hero/hero-logos-jp'
    : currentLanguage === 'vi' ? '/images/home/hero/hero-logos-vn'
    : '/images/home/hero/hero-logos';

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <main className="relative">
        {/* Hero Section - Full Width Banner */}
        <section id="home" className="relative">
          <div className="relative w-full">
            {/* Banner Image */}
            <picture>
              <source
                srcSet="/images/home/hero/hero-banner-new.webp"
                type="image/webp"
              />
              <img
                src="/images/home/hero/hero-banner-new.png"
                alt="Hero Background"
                className="w-full h-auto"
                style={{ minHeight: '117px', maxHeight: '467px', objectFit: 'cover' }}
              />
            </picture>

            {/* Text container aligned with main content (max-w-7xl) */}
            <div className="absolute inset-x-0 top-0 bottom-0 pointer-events-none">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full relative">
                {/* Title & Subtitle positioned within the content container */}
                <div className="absolute pointer-events-auto top-[25%] xs:top-[20%]" style={{
                  width: '49.75%',
                  maxHeight: '46.6%'
                }}>
                  <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    <h1 className="text-base xxxs:text-xl xxs:text-2xl xs:text-2xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight text-foreground">
                      {t('home.heroTitle')}
                      {t('home.heroHighlight') && (
                        <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                          {" "}{t('home.heroHighlight')}
                        </span>
                      )}
                    </h1>

                    <p className="hidden sm:block text-xs xxs:text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground leading-tight">
                      {t('home.heroSubtitle')}
                    </p>
                  </motion.div>
                </div>

                {/* Social Media Logos - original PSD layer */}
                {/* Mobile (< 480px): right side below graphic, Desktop (≥ 480px): bottom left */}
                <div className="absolute pointer-events-auto left-[51%] xs:left-[2%] top-[79%] xs:top-[65.6%] w-[47%] xs:w-[39.84%] h-[20%] xs:h-[11.86%]">
                  <picture>
                    <source srcSet={`${heroLogos}.webp?v=2`} type="image/webp" />
                    <img
                      src={`${heroLogos}.png?v=2`}
                      alt="Social Media Platforms"
                      className="w-full h-full object-contain object-left"
                    />
                  </picture>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search Evolution Section - Japanese Only */}
        {currentLanguage === 'ja' && (
          <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
              <motion.div
                className="text-left space-y-6 mb-12"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl lg:text-5xl text-foreground">
                  消費者の情報取得手段はSearch3.0の時代へ
                </h2>
                <p className="text-lg text-muted-foreground max-w-4xl">
                  ユーザーの「検索習慣」の変化は、必然的にブランドのマーケティング手法に変革を迫ります。企業にとって重要なのは、この変化した情報消費スタイルに寄り添い、新たなマーケティングの潮流に乗ることです。
                </p>
              </motion.div>

              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <img
                  src="/images/home/search-evolution-jp.png"
                  alt="Search 3.0 時代への進化"
                  className="w-full max-w-5xl"
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* Our Services Section */}
        <section id="services" className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-left space-y-6 mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('home.ourServices')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl">
                {t('home.ourServicesDescription')}
              </p>
            </motion.div>

            {/* JP: Show 3-solutions image with clickable hotspots, Other languages: Show 4 cards */}
            {currentLanguage === 'ja' ? (
              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="relative w-full max-w-5xl">
                  <img
                    src="/images/home/3-solutions-jp.png"
                    alt="3つのテクノロジーマーケティングソリューション"
                    className="w-full"
                  />
                  {/* Clickable Hotspot 1: GEO Marketing (top) */}
                  <div
                    onClick={() => navigate('/geo-solutions')}
                    className="absolute cursor-pointer rounded-[50%] transition-all duration-300 hover:bg-[radial-gradient(ellipse,rgba(6,182,212,0.15)_0%,transparent_70%)]"
                    style={{
                      top: '0%',
                      left: '15%',
                      width: '70%',
                      height: '35%',
                    }}
                    title="GEO Marketing (AI Search)"
                  />
                  {/* Clickable Hotspot 2: SNS & O2O Growth Engine (bottom-right) */}
                  <div
                    onClick={() => navigate('/technology-marketing-solutions')}
                    className="absolute cursor-pointer rounded-[50%] transition-all duration-300 hover:bg-[radial-gradient(ellipse,rgba(6,182,212,0.15)_0%,transparent_70%)]"
                    style={{
                      top: '52%',
                      right: '0%',
                      width: '45%',
                      height: '48%',
                    }}
                    title="SNS & O2O Growth Engine"
                  />
                  {/* Clickable Hotspot 3: Social Commerce & EC Loop (bottom-left) */}
                  <div
                    onClick={() => navigate('/technology-marketing-solutions')}
                    className="absolute cursor-pointer rounded-[50%] transition-all duration-300 hover:bg-[radial-gradient(ellipse,rgba(6,182,212,0.15)_0%,transparent_70%)]"
                    style={{
                      top: '52%',
                      left: '0%',
                      width: '45%',
                      height: '48%',
                    }}
                    title="Social Commerce & EC Loop"
                  />
                </div>
              </motion.div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* Social Media Marketing */}
                <motion.div
                  className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-500 cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  onClick={() => navigate('/technology-marketing-solutions')}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-slate-800/20 rounded-full mb-6 flex items-center justify-center">
                    <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </div>
                  <h3 className="text-xl mb-3 text-foreground font-semibold">
                    {t('home.serviceBoxSocialMediaTitle')}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('home.serviceBoxSocialMediaDesc')}
                  </p>
                </motion.div>

                {/* KOL/KOC Marketing - navigates to Social Media Marketing page's KOL section */}
                <motion.div
                  className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-500 cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  onClick={() => {
                    // Navigate with hash to trigger scroll in SocialMediaMarketing component
                    navigate('/technology-marketing-solutions#kol-marketing');
                  }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-slate-800/20 to-cyan-400/20 rounded-full mb-6 flex items-center justify-center">
                    <svg className="w-8 h-8 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl mb-3 text-foreground font-semibold">
                    {t('home.serviceBoxKolTitle')}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('home.serviceBoxKolDesc')}
                  </p>
                </motion.div>

                {/* Baidu Search & Media Marketing */}
                <motion.div
                  className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-500 cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                  onClick={() => navigate('/chinese-search-ads-media-outreach')}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400/20 to-slate-800/20 rounded-full mb-6 flex items-center justify-center">
                    <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl mb-3 text-foreground font-semibold">
                    {t('home.serviceBoxBaiduTitle')}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('home.serviceBoxBaiduDesc')}
                  </p>
                </motion.div>

                {/* More to come */}
                <motion.div
                  className="bg-gradient-to-br from-slate-50 to-cyan-50 border border-slate-200/60 rounded-xl p-8 transition-all duration-500"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="w-16 h-16 bg-white rounded-full mb-6 flex items-center justify-center shadow-sm">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-xl mb-3 text-foreground font-semibold">
                    {t('home.serviceBoxMoreTitle')}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('home.serviceBoxMoreDesc')}
                  </p>
                </motion.div>
              </div>
            )}
          </div>
        </section>

        {/* Why Choose Z Creative Partners Section - Full Width */}
        <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-50/30 to-slate-100/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Title */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground mb-4">
                {t('home.whyChooseTitle')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('home.whyChooseSubtitle')}
              </p>
            </motion.div>

            {/* Metrics Section */}
            <motion.div
              className="mb-20"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              onViewportEnter={() => setMetricsInView(true)}
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                {/* Top Row Metrics */}
                {[
                  { number: 100, suffix: "%", label: t('home.contractRenewalRate'), color: "from-cyan-500 to-cyan-600" },
                  { number: 95, suffix: "%", label: t('home.clientSatisfactionRate'), color: "from-slate-700 to-slate-800" },
                  { number: 75, suffix: "%", label: t('home.referralRate'), sublabel: t('home.referralRateSub'), color: "from-cyan-600 to-slate-700" },
                  { number: 100, suffix: "+", label: t('home.brandsSupported'), color: "from-slate-800 to-cyan-600" }
                ].map((metric, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl p-6 shadow-lg border border-slate-100"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <motion.div
                      className={`text-4xl lg:text-5xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent mb-2`}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      viewport={{ once: true }}
                    >
                      <AnimatedCounter end={metric.number} suffix={metric.suffix} duration={1.0} inView={metricsInView} />
                    </motion.div>
                    <div className="text-sm text-foreground font-medium">
                      {metric.label.split('\n').map((line, i, arr) => (
                        <span key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </span>
                      ))}
                    </div>
                    {metric.sublabel && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {metric.sublabel.split('\n').map((line, i, arr) => (
                          <span key={i}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {/* Bottom Row Metrics */}
                {[
                  { number: 10, suffix: "+", label: t('home.industriesSupported'), color: "from-cyan-500 to-slate-700" },
                  { number: 9000, suffix: "+", prefix: "", label: t('home.snsPostsCreated'), sublabel: t('home.snsPostsCreatedSub'), color: "from-slate-700 to-cyan-600", isLarge: true },
                  { number: 100, suffix: "%", label: t('home.contentOwnership'), sublabel: t('home.contentOwnershipSub'), color: "from-cyan-600 to-cyan-500" },
                  { number: 100, suffix: "%", label: t('home.alignedWithTrends'), sublabel: t('home.alignedWithTrendsSub'), color: "from-slate-800 to-slate-700" }
                ].map((metric, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl p-6 shadow-lg border border-slate-100"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <motion.div
                      className={`text-4xl lg:text-5xl font-bold bg-gradient-to-r ${metric.color} bg-clip-text text-transparent mb-2`}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      viewport={{ once: true }}
                    >
                      {metric.isLarge ? (
                        <><AnimatedCounter end={metric.number} format={true} duration={1.0} inView={metricsInView} />{metric.suffix}</>
                      ) : (
                        <AnimatedCounter end={metric.number} suffix={metric.suffix} prefix={metric.prefix || ""} duration={1.0} inView={metricsInView} />
                      )}
                    </motion.div>
                    <div className="text-sm text-foreground font-medium">
                      {metric.label.split('\n').map((line, i, arr) => (
                        <span key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </span>
                      ))}
                    </div>
                    {metric.sublabel && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {metric.sublabel.split('\n').map((line, i, arr) => (
                          <span key={i}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Our Key Strengths Subsection */}
            <motion.div
              className="mt-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl lg:text-3xl text-foreground mb-8 text-center font-semibold">
                {t('home.ourKeyStrengths')}
              </h3>
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {[
                  { 
                    title: t('home.strengthGlobalTeamTitle'),
                    description: t('home.strengthGlobalTeamDesc'),
                    icon: "M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"
                  },
                  { 
                    title: t('home.strengthExpertTeamTitle'),
                    description: t('home.strengthExpertTeamDesc'),
                    icon: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  },
                  { 
                    title: t('home.strengthIndustryTitle'),
                    description: t('home.strengthIndustryDesc'),
                    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  },
                  { 
                    title: t('home.strengthTailoredTitle'),
                    description: t('home.strengthTailoredDesc'),
                    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-4 bg-white rounded-xl p-6 shadow-md"
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.6 + index * 0.1,
                    }}
                    viewport={{ once: true }}
                  >
                    <div 
                      className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-2">
                        {item.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

          </div>
        </section>

        {/* Our Clients Section - Auto-sliding Carousel */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('home.ourValuedBrands')}
              </h2>
            </motion.div>

            {/* Brand Logos Carousel */}
            <BrandCarousel />
            
            <motion.p 
              className="text-xs text-gray-400 mt-8 text-center max-w-4xl mx-auto px-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {t('home.brandDisclaimer')}
            </motion.p>
          </div>
        </section>
      </main>
    </div>
  );
}